<template>
  <div
    ref="sticky"
    class="ui-heading"
    :class="[
      `ui-heading--${background}`,
      { 'ui-heading--allow-sticky': allowSticky, 'ui-heading--is-sticky': isSticky, 'ui-heading--is-specific': isSpecific }
    ]"
    :style="[allowSticky ? { height: stickyBarHeight + 'px' } : {}]"
  >
    <ui-box
      class="ui-heading__ui-box"
      ref="stickyBox"
      :class="{
        'py-12px': $media.mobile || isSticky,
        'py-24px': $media.tablet && !isSticky,
        'ui-heading__ui-box--allow-sticky': allowSticky,
        'ui-heading__ui-box--is-sticky': isSticky,
        'ui-heading__ui-box--is-specific': isSpecific
      }"
    >
      <div class="level">
        <!-- Col left -->
        <div class="level-left ui-heading__col" :class="[twoCols ? 'pr-8px' : 'ui-heading__col--single pr-0']">
          <div
            class="level-item is-block ui-heading__level-item"
            :class="[{ 'pr-24px': $media.tablet && twoCols }, { 'ui-heading__level-item--single': !twoCols }]"
          >
            <slot name="suptitle" :isSticky="isSticky">
              <div v-if="suptitle" class="is-size-6">{{ suptitle }}</div>
            </slot>
            <slot name="default" :isSticky="isSticky">
              <h1 v-if="title" class="title is-2 mb-16px has-text-weight-bold">{{ title }}</h1>
            </slot>
            <slot name="subtitle" :isSticky="isSticky">
              <p v-if="subtitle" class="title is-3 has-text-weight-light">{{ subtitle }}</p>
            </slot>
            <slot name="details" :isSticky="isSticky">
              <p v-if="details" class="is-size-7">{{ details }}</p>
            </slot>
          </div>
        </div>
        <!-- Col right -->
        <div
          v-if="twoCols"
          class="level-right"
          :class="{
            'mt-2': $media.mobile,
            'mt-0': $media.tablet
          }"
        >
          <div class="level-item ui-heading__actions">
            <slot name="actions" :isSticky="isSticky"></slot>
          </div>
        </div>
      </div>
    </ui-box>
  </div>
</template>

<script>
import UiBox from '@/components/ui/box'

export default {
  components: {
    UiBox
  },
  props: {
    suptitle: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    },
    details: {
      type: String,
      default: null
    },
    twoCols: {
      type: Boolean,
      default: false
    },
    background: {
      type: String,
      default: 'primary'
    },
    allowSticky: {
      type: Boolean,
      default: false
    },
    isSpecific: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isSticky: false,
      stickyBarHeight: 0
    }
  },
  created() {
    if (this.allowSticky) {
      window.addEventListener('resize', this.resizeStickyBar)
    }
  },
  destroyed() {
    if (this.allowSticky) {
      window.removeEventListener('resize', this.resizeStickyBar)
    }
  },
  mounted() {
    if (this.allowSticky) {
      this.$nextTick(() => {
        if (!this.$refs.sticky) return

        this.setIntervalOnResize()

        const stickyBar = this.$refs.sticky

        const options = {
          root: null,
          rootMargin: '0px 0px 0px 0px',
          threshold: 1
        }

        const handler = (entries) => {
          if (entries[0].intersectionRatio != 1) {
            this.isSticky = true
          } else {
            this.isSticky = false
            this.resizeStickyBar()
          }
        }
        const observer = new window.IntersectionObserver(handler, options)
        observer.observe(stickyBar)
      })
    }
  },
  methods: {
    setIntervalOnResize() {
      // HOTFIX pour resizer la hauteur du heading sans connaître d'avance la largeur du dropdown à l'intérieur
      const resizeBar = setInterval(this.resizeStickyBar, 200)
      const stopResizingBar = () => {
        if (window.scrollY > 0) {
          clearInterval(resizeBar)
        }
      }
      // Dès le premier mouvement de scroll, on arrête de redimensionner le heading, parce que l'intersectionObserver s'en occupe.
      window.addEventListener('scroll', stopResizingBar, { once: true })
    },
    resizeStickyBar() {
      this.$nextTick(() => {
        if (!this.$refs.stickyBox || !this.$refs.stickyBox.$el) return
        const stickyBox = this.$refs.stickyBox.$el
        this.stickyBarHeight = stickyBox.offsetHeight
      })
    }
  }
}
</script>

<style lang="scss">
.ui-heading {
  border-color: $primary;
  background-color: $white;
  border-top-style: solid;
  border-top-width: 5px;

  &--primary {
    background: linear-gradient(90deg, #e6ecf4 22.71%, rgba(248, 249, 252, 0) 100%);
  }

  &--is-specific {
    border-color: $turquoise;
  }

  &--single-col {
    justify-content: stretch;
  }

  &--allow-sticky {
    background-color: transparent;
    border-top-width: 0;
    position: relative;
    padding: 0;
    transition: padding 0.2s ease-in-out, height 0.4s ease-in-out;
    pointer-events: none;
  }

  &--is-sticky {
    background-color: transparent;
    top: -1px; // trigger sticky observer
    padding-top: 53px; // appears under menu bar on mobile
    position: sticky;
    z-index: 21;

    @include desktop {
      padding-top: 0;
      z-index: 31;
    }
  }

  &__ui-box {
    &--allow-sticky {
      background-color: $white;
      border-top: 4px solid $primary;
      border-bottom: 1px solid transparent;
      pointer-events: auto;

      &.ui-heading__ui-box--is-specific {
        border-top-color: $turquoise;
      }
    }

    &--is-sticky {
      box-shadow: 0 0.5em 1em -0.125em rgba(0, 0, 0, 0.1), 0 0px 0 1px rgba(0, 0, 0, 0.02);

      @include desktop {
        border-top-width: 9px;
      }
    }
  }

  &__col {
    flex-shrink: 1;

    &--single {
      flex-grow: 1;
    }
  }

  &__level-item {
    flex-shrink: 1;

    & > :first-child {
      margin-top: 0 !important;
    }

    & > :last-child {
      margin-bottom: 0 !important;
    }

    &--single {
      width: 100%;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    @include tablet {
      align-items: flex-end;
    }
  }
}
</style>